import React from 'react';
import { 
  Target, 
  Zap, 
  Shield, 
  Timer,
  CheckCircle2,
  Microscope,
  Brain,
  Activity,
  HeartPulse,
  Sparkles
} from 'lucide-react';
import './Sc4f.css';

const Sc4f: React.FC = () => {
  return (
    <div className="sc4f">
      {/* Hero Section */}
      <div className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Advanced Cancer Treatment Success Stories</h1>
          <p>Breakthrough Technologies in Modern Cancer Therapy</p>
        </div>
      </div>

      {/* BNCT Case Study */}
      <section className="case-section container">
        <div className="case-header">
          <h2>Case Study #1: Boron Neutron Capture Therapy (BNCT)</h2>
          <p className="subtitle">Advanced Precision in Cancer Treatment</p>
        </div>

        <div className="video-container">
          <iframe
            src="https://www.youtube.com/embed/399OlHiC4AE"
            title="BNCT Treatment Overview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <Target className="feature-icon" />
            <h3>Enhanced Precision</h3>
            <p>Two-step mechanism ensures precise targeting of cancer cells while sparing healthy tissue</p>
            <ul className="feature-list">
              <li>Selective boron drug accumulation</li>
              <li>Neutron-triggered targeted destruction</li>
              <li>5-10 micrometer precision range</li>
            </ul>
          </div>

          <div className="feature-card">
            <Shield className="feature-icon" />
            <h3>Minimal Side Effects</h3>
            <p>Significantly reduced side effects compared to traditional radiotherapy</p>
            <ul className="feature-list">
              <li>Single treatment session</li>
              <li>Targeted cell destruction</li>
              <li>Reduced radiation exposure</li>
            </ul>
          </div>

          <div className="feature-card">
            <Brain className="feature-icon" />
            <h3>Complex Tumor Treatment</h3>
            <p>Ideal for tumors with unclear boundaries and infiltrative characteristics</p>
            <ul className="feature-list">
              <li>Effective for glioblastoma</li>
              <li>Treats recurrent cancers</li>
              <li>Works on difficult locations</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Heavy Ion Therapy Case */}
      <section className="case-section container">
        <div className="case-header">
          <h2>Case Study #2: Heavy Ion Therapy</h2>
          <p className="subtitle">Taipei Veterans General Hospital's Advanced Cancer Treatment</p>
        </div>

        <div className="video-container">
          <iframe
            src="https://www.youtube.com/embed/ZVq0GQ5HlIs"
            title="Heavy Ion Therapy Overview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>

        <div className="treatment-details">
          <div className="details-grid">
            <div className="detail-card">
              <Zap className="detail-icon" />
              <h3>Superior Energy Delivery</h3>
              <p>Carbon ions deliver precise energy at tumor sites through the Bragg Peak effect</p>
            </div>

            <div className="detail-card">
              <Activity className="detail-icon" />
              <h3>Enhanced Effectiveness</h3>
              <p>Direct DNA damage to cancer cells with minimal repair possibility</p>
            </div>

            <div className="detail-card">
              <Timer className="detail-icon" />
              <h3>Efficient Treatment</h3>
              <p>Fewer sessions required compared to conventional radiotherapy</p>
            </div>

            <div className="detail-card">
              <HeartPulse className="detail-icon" />
              <h3>Broad Application</h3>
              <p>Effective against various resistant cancers including sarcomas and liver cancer</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="comparison-section">
        <div className="container">
          <h2 className="section-title">Treatment Comparison</h2>
          <div className="comparison-grid">
            <div className="comparison-card">
              <h3>Traditional Radiotherapy</h3>
              <ul className="comparison-list">
                <li>Multiple treatment sessions</li>
                <li>Affects surrounding tissue</li>
                <li>Limited effectiveness on resistant tumors</li>
                <li>More side effects</li>
              </ul>
            </div>

            <div className="comparison-card highlight">
              <h3>Advanced Therapies (BNCT & Heavy Ion)</h3>
              <ul className="comparison-list">
                <li>Single or fewer sessions</li>
                <li>Precise tumor targeting</li>
                <li>Effective on resistant cancers</li>
                <li>Minimal side effects</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="results-section container">
        <h2 className="section-title">Treatment Outcomes</h2>
        <div className="results-grid">
          <div className="result-card">
            <Microscope className="result-icon" />
            <h3>Precision Treatment</h3>
            <p>Advanced targeting mechanisms ensure maximum effectiveness with minimal tissue damage</p>
          </div>

          <div className="result-card">
            <CheckCircle2 className="result-icon" />
            <h3>High Success Rate</h3>
            <p>Improved outcomes for previously difficult-to-treat cancers</p>
          </div>

          <div className="result-card">
            <Sparkles className="result-icon" />
            <h3>Quality of Life</h3>
            <p>Better patient experience with fewer side effects and shorter treatment duration</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sc4f;